import React, { Fragment, useContext, useEffect, useState } from 'react';

import cx from 'classnames';
import { isEmpty } from 'ramda';

import { FlightsContext } from 'components/Pages/Admin/GuestPortal/Flights/context';
import { modalContentNomad } from 'components/Pages/Admin/GuestPortal/Landing/Guests/index.module.css';
import Modal from 'components/shared/Modal';
import tableStyles from 'components/shared/Table/index.module.css';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import i18n from 'i18n';
import { LANDING_THEMES } from 'utils/itineraries/functions';

import { AddFlights } from '../AddFlights';
import { AddManually } from '../AddManually';
import { WhatAdd } from '../WhatAdd';
import s from './index.module.css';
import { RowContent } from './RowContent';
import { TableButtons } from './TableButtons';

function TableHead() {
  return (
    <thead className={cx(tableStyles.thead, 'custom-flights-thead')}>
      <tr>
        <th style={{ width: '0', padding: '0' }} />
        <th
          className={cx(tableStyles.cell, tableStyles.th, s.th, 'custom-flights-th', s.dateTh, 'custom-flights-dateTh')}
        >
          {i18n.t('proposal_content.guest_portal.flight_and_transfers_page.tabs.date')}
        </th>
        <th
          className={cx(
            tableStyles.cell,
            tableStyles.th,
            s.th,
            s.detailsTh,
            'custom-flights-th',
            'custom-flights-detailsTh',
          )}
        >
          {i18n.t('proposal_content.guest_portal.flight_and_transfers_page.tabs.details')}
        </th>
        <th
          className={cx(
            tableStyles.cell,
            tableStyles.th,
            s.th,
            s.notesTh,
            'custom-flights-th',
            'custom-flights-notesTh',
          )}
        >
          {i18n.t('proposal_content.guest_portal.flight_and_transfers_page.tabs.notes')}
        </th>
        <th style={{ width: '0', padding: '0' }} />
      </tr>
    </thead>
  );
}

function TableList({ collection, isReadonly, withoutMenu, isPDF = false, dateFormat }) {
  const { isTabletOrMobile } = useMediaBreakpoints();

  if (isTabletOrMobile)
    return collection.map((item, i) => (
      <RowContent
        key={i}
        isReadonly={isReadonly}
        item={item}
        itemIndex={i}
        withoutMenu={withoutMenu}
        isPDF={isPDF}
        dateFormat={dateFormat}
      />
    ));

  return collection.map((item, i) => (
    <RowContent
      key={i}
      isReadonly={isReadonly}
      item={item}
      itemIndex={i}
      withoutMenu={withoutMenu}
      isPDF={isPDF}
      dateFormat={dateFormat}
    />
  ));
}

function TableMain({ children }) {
  return <table className={cx(tableStyles.table, s.table, 'custom-flights-table')}>{children}</table>;
}

function TableBody({ children, disabled }) {
  return (
    <tbody className={cx(tableStyles.tbody, s.tbody, disabled && s.disabled, 'custom-flights-table-body')}>
      {children}
    </tbody>
  );
}

function TableWrapper({ children, wrapperClassName, onClick = () => {} }) {
  return (
    <div className={cx(s.tableWrapper, wrapperClassName, 'custom-flights-table-wrapper')} onClick={onClick}>
      {children}
    </div>
  );
}

function Table({
  isReadonly,
  items,
  withoutMenu = false,
  onDisableClick = () => {},
  showSecondButton = false,
  wrapperClassName,
  landingTheme,
  disabled,
  dateFormat,
}) {
  const {
    openWhatAddModal,
    modalData,
    isModalOpen,
    closeModal,
    isModalFlightsOpened,
    isModalManuallyOpened,
    isModalWhatAddOpened,
  } = useContext(FlightsContext);
  const filteredItems = items.filter((item) => !item._destroy);
  const isNoData = isEmpty(filteredItems);

  // TODO fix: hack to rerender the table, as the old translations remain
  const [, forceRefresh] = useState(0);

  useEffect(() => {
    forceRefresh((prev) => prev + 1);
  }, []);

  return (
    <Fragment>
      <TableWrapper onClick={onDisableClick} wrapperClassName={wrapperClassName}>
        <TableMain>
          <TableHead />
          <TableBody disabled={disabled}>
            {isNoData ? (
              <tr>
                <td style={{ width: '0' }} />
                <td
                  colSpan={3}
                  className={cx(
                    tableStyles.cell,
                    tableStyles.cellTd,
                    tableStyles.noDataText,
                    s.td,
                    'custom-flights-cell',
                    'custom-flights-cellTd',
                    'custom-flights-noDataText',
                  )}
                >
                  No data added
                </td>
                <td style={{ width: '0' }} />
              </tr>
            ) : (
              <TableList
                collection={filteredItems}
                isReadonly={isReadonly}
                withoutMenu={withoutMenu}
                dateFormat={dateFormat}
              />
            )}
          </TableBody>
        </TableMain>

        <TableButtons
          disabled={disabled}
          isReadonly={isReadonly}
          openWhatAddModal={openWhatAddModal}
          showSecondButton={showSecondButton}
        />

        <Modal
          isOpen={Boolean(isModalOpen)}
          toClose={closeModal}
          size={isModalWhatAddOpened ? 'md' : 'lgm'}
          contentClassName={cx(
            'custom-relocations-form-modal-content',
            landingTheme && landingTheme !== LANDING_THEMES.default && modalContentNomad,
          )}
        >
          {isModalFlightsOpened && <AddFlights {...modalData} />}
          {isModalManuallyOpened && <AddManually {...modalData} />}
          {isModalWhatAddOpened && <WhatAdd {...modalData} />}
        </Modal>
      </TableWrapper>
    </Fragment>
  );
}

export const FlightsTable = Table;

FlightsTable.Head = TableHead;
FlightsTable.List = TableList;
FlightsTable.Table = TableMain;
FlightsTable.Body = TableBody;
FlightsTable.Wrapper = TableWrapper;
